 import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Input,
  Alert,
  Progress,
  Divider,
  Typography,
  Space,
  theme,
  message,
} from "antd";
import { UserSwitchOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const CharacterScanModal = ({ visible, onClose, initialName = '' }) => {
  const [characterName, setCharacterName] = useState('');
  const [characterData, setCharacterData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);


  const { token } = theme.useToken(); // para dark mode

  const isMobile = window.innerWidth < 480;

  const handleSearch = useCallback(async (nameParam) => {
    const name = nameParam ?? characterName;
    if (!name.trim()) {
      message.warning("Escribe el nombre del char antes de buscar.");
      return;
    }

    setLoading(true);
    setNotFound(false);
    setCharacterData(null);
    setProgressPercent(0);

    try {
      const encodedName = encodeURIComponent(name.trim());

      // 1️⃣ Registrar o personagem (body correto)
      const registerRes = await fetch(
        "https://api.tibspy.com/api/private/v1/report/register",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: `characterName=${encodedName}`,
        }
      );

      if (!registerRes.ok) {
        throw new Error("Error al registrar personaje");
      }

      const registerData = await registerRes.json();
      if (!registerData.jobId) {
        throw new Error("No se recibió jobId válido");
      }

      const jobId = registerData.jobId;

      // 2️⃣ Esperar até o status mudar para READY
      let status = "IN_PROGRESS";
      let retries = 0;
      const maxRetries = 30; // 90 segundos no total

      while (status === "IN_PROGRESS" && retries < maxRetries) {
        await new Promise((res) => setTimeout(res, 3000));

        const statusRes = await fetch(
          `https://api.tibspy.com/api/private/v1/report/${jobId}/status`
        );

        const statusData = await statusRes.json();
        status = statusData.reportDataStatus;

        retries++;
        const percent = Math.min(
          Math.round((retries / maxRetries) * 100),
          99
        );
        setProgressPercent(percent);
      }

      if (status !== "READY") {
        throw new Error("Tiempo de espera agotado, intenta nuevamente.");
      }

      setProgressPercent(100);

      // 3️⃣ Buscar histórico do personagem
      const historyRes = await fetch(
        `https://api.tibspy.com/api/private/v1/report/history/${encodedName}`
      );

      if (!historyRes.ok) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const historyData = await historyRes.json();
      if (!Array.isArray(historyData) || historyData.length === 0) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // 4️⃣ Filtrar o registro que corresponde ao jobId atual
      const target = historyData.find((item) => item.jobId === jobId);
      if (!target) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const parsed = JSON.parse(target.jsonData);

      const formattedData = {
        name: parsed.scannedCharacter,
        possibleInvisibleCharacters: parsed.characters.map((c) => ({
          otherCharacterName: c.name,
          numberOfMatches: c.visibleCounter,
        })),
      };

      setCharacterData(formattedData);
    } catch (error) {
      console.error("Error al buscar personaje:", error);
      setNotFound(true);
    }

    setLoading(false);
  }, [characterName]); // dependência principal

  // Quando abrir o modal e tiver nome inicial, faz busca automática
  useEffect(() => {
    if (visible && initialName) {
      const restoredName = initialName.replace(/_/g, ' ');
      setCharacterName(restoredName);
      handleSearch(restoredName);
    }
  }, [visible, initialName, handleSearch]);

  const handleClose = () => {
    // 👇 Limpa os dados só ao fechar
    setCharacterName('');
    setCharacterData(null);
    setNotFound(false);
    setLoading(false);
    onClose();
  };

  const calcularPorcentagem = (matches) => {
    if (matches >= 30) return 99;
    if (matches >= 20) return 80;
    if (matches >= 15) return 65;
    if (matches >= 10) return 40;
    if (matches >= 5) return 25;
    return 10;
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
      width={550}
      title="Escaneo de Personajes"
    >
      <Input.Search
        placeholder="Escribe el nombre del personaje"
        enterButton="Buscar"
        //allowClear
        value={characterName}
        onChange={(e) => setCharacterName(e.target.value)}
        onSearch={handleSearch}
        style={{ marginTop: 8, marginBottom: 5 }}
      />

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Text strong>
            {progressPercent < 100
              ? "Escaneando personaje..."
              : "Cargando resultados..."}
          </Text>
          <br />
          <Progress
            percent={progressPercent}
            style={{ width: 200, marginTop: 10 }}
            status={progressPercent < 100 ? "active" : "success"}
          />
          <Text type="secondary" style={{ display: "block", marginTop: 6 }}>
            {progressPercent < 100
              ? "Esto puede tardar unos segundos..."
              : "Procesamiento completado."}
          </Text>
        </div>
      ) : notFound ? (

        <Alert
          type="error"
          message="Personaje no encontrado"
          description="Verifica el nombre escrito e intenta nuevamente."
          showIcon
        />
      ) : characterData ? (

        <>

            <Divider style={{ margin: "20px 0" }} />

            <Title level={5}>
                <UserSwitchOutlined style={{ marginRight: 8 }} />
                Posibles personajes
            </Title>

            {characterData.possibleInvisibleCharacters?.length ? (
                <div style={{ maxHeight: 300, overflowY: "auto", paddingRight: 10 }}>
                {characterData.possibleInvisibleCharacters.map((char, index) => {
                    const percent = calcularPorcentagem(char.numberOfMatches);
                    return (
                    <div
                        key={index}
                        style={{
                        background: token.colorBgContainer,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: 6,
                        padding: 8,
                        marginBottom: 8,
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "space-between",
                        alignItems: isMobile ? "flex-start" : "center",
                        gap: 2,
                        }}
                    >
                        <Space
                        size="middle"
                        direction={isMobile ? "vertical" : "horizontal"}
                        style={{ flex: 1 }}
                        >
                        <a
                            href={`https://www.tibia.com/community/?name=${encodeURIComponent(
                            char.otherCharacterName
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontWeight: "bold", fontSize: 13, wordBreak: "break-word" }}
                        >
                            {char.otherCharacterName}
                        </a>
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                            {char.numberOfMatches} matches {char.firstMatchDateOnly} {char.lastMatchDateOnly}
                        </Text>
                        </Space>

                        <div style={{ width: isMobile ? "100%" : 80 }}>
                        <Progress
                            percent={percent}
                            size="small"
                            strokeColor={
                            percent >= 80 ? "#52c41a" : percent >= 50 ? "#faad14" : "#f5222d"
                            }
                            showInfo={false}
                        />
                        </div>
                    </div>
                    );
                })}
                </div>
            ) : (
                <Text type="secondary">No se detectaron personajes invisibles.</Text>
            )}
        </>

      ) : (
        <p style={{ textAlign: "center", color: "#999" }}>
          Escribe el nombre de un personaje para ver sus datos.
        </p>
      )}
    </Modal>
  );
};

export default CharacterScanModal;